import { CompositeSceneRenderer } from '../renderer';
import { BoneCollisionBody, PhysicsFeature } from '../physics';
import { RenderObject } from '../object/RenderObject';
import { VisualPathNodes } from './VisualPathNodes';
import { VisualQuadTreeGrid } from './VisualQuadTreeGrid';
import { SkinnedSkeletonVisualizer } from './SkinnedSkeletonVisualizer';
import type { Scene } from '../scene/Scene';

export function setupSceneDebug(scene: Scene, renderer: CompositeSceneRenderer): void {
  scene.debugManager.add('navigation-graph', false).stateChanged.subscribe((dbg) => {
    if (dbg.enabled) {
      const MAX_VIZ_NODES = 5000;
      const nodes = scene.navigation.getAllNodes();
      if (nodes.length > MAX_VIZ_NODES) {
        console.warn(
          `Navigation graph too large for debug viz (${nodes.length} nodes, max ${MAX_VIZ_NODES}). Skipping.`,
        );
        return;
      }
      let debugPathNodes = new VisualPathNodes(scene.ctx);
      debugPathNodes.setPathNodes([...nodes]);
      dbg.objects = [debugPathNodes];
    }
  });

  scene.debugManager.add('volume-grid', false).stateChanged.subscribe((dbg) => {
    if (dbg.enabled) {
      if (!scene.volumeGrid) {
        console.warn('Volume grid not loaded — no .volgrid in scene manifest?');
        return;
      }
      let viz = new VisualQuadTreeGrid(scene.ctx);
      viz.setVolumeGrid(scene.volumeGrid);
      dbg.objects = [viz];
    }
  });

  let skeletonState:
    | {
        visualizers: Map<RenderObject, SkinnedSkeletonVisualizer>;
        tick: { cancel(): void };
      }
    | undefined;

  scene.debugManager.add('skeleton', false).stateChanged.subscribe((dbg) => {
    if (dbg.enabled) {
      const visualizers = new Map<RenderObject, SkinnedSkeletonVisualizer>();
      const tick = scene.tickEventManager.registerFrameEvent(() => {
        updateSkeletonOverlay(scene, visualizers);
      });
      skeletonState = { visualizers, tick };
    } else if (skeletonState) {
      skeletonState.tick.cancel();
      for (const viz of skeletonState.visualizers.values()) {
        scene.objectManager.removeObject(viz);
        viz.destroy();
      }
      skeletonState.visualizers.clear();
      dbg.objects = [];
      skeletonState = undefined;
    }
  });

  // The 'fps' debug toggle is just a visibility flag. Apps render their own
  // perf overlay UI (in playful, see PerfOverlay) by watching this option's
  // enabled state and reading renderer.deferredRenderer.renderStats.
  scene.debugManager.add('fps', false);

  // Hide all animated (skinned) render objects from the rendering pipeline.
  // Used to A/B-test the cost of skinning + skinned shadow casting without
  // having to find a camera angle that excludes the characters.
  scene.debugManager.add('hide-animated', false).stateChanged.subscribe((dbg) => {
    scene.objectManager.renderFilter = dbg.enabled
      ? (obj) => !(obj instanceof RenderObject && obj.isAnimated)
      : null;
  });

  scene.debugManager
    .add('ssao', true)
    .stateChanged.subscribe((dbg) => (renderer.deferredRenderer.ssaoEnabled = dbg.enabled));
  scene.debugManager
    .add('god-rays', true)
    .stateChanged.subscribe(
      (dbg) => (renderer.deferredRenderer.godRaysEnabled = dbg.enabled),
    );
  scene.debugManager
    .add('volumetric', true)
    .stateChanged.subscribe(
      (dbg) => (renderer.deferredRenderer.volumetricEnabled = dbg.enabled),
    );
  scene.debugManager
    .add('fxaa', true)
    .stateChanged.subscribe((dbg) => (renderer.deferredRenderer.fxaaEnabled = dbg.enabled));
}

/**
 * Reconciles the live set of animated render objects with the set of skeleton
 * debug visualizers. Discovers animated objects directly from the scene's
 * object manager, so any object with an AnimationState shows the overlay —
 * regardless of whether physics is active or a BoneCollisionBody is
 * registered. When physics is present and an object happens to have a
 * registered body, its bone capsules are drawn too.
 */
function updateSkeletonOverlay(
  scene: Scene,
  visualizers: Map<RenderObject, SkinnedSkeletonVisualizer>,
): void {
  const physics = scene.features.get('physics') as PhysicsFeature | undefined;
  const bodyByObject = new Map<RenderObject, BoneCollisionBody>();
  if (physics) {
    for (const body of physics.boneHitDetection.targets) {
      bodyByObject.set(body.renderObject, body);
    }
  }

  const live = new Set<RenderObject>();
  for (const obj of scene.objectManager.getAllObjects()) {
    if (!(obj instanceof RenderObject)) continue;
    if (!obj.isAnimated || !obj.animationState || !obj.animation) continue;
    if (!obj.mesh.animationDataTexture) continue;
    live.add(obj);
  }

  // Remove visualizers for objects that are no longer animated / removed.
  for (const [ro, viz] of visualizers) {
    if (!live.has(ro)) {
      scene.objectManager.removeObject(viz);
      viz.destroy();
      visualizers.delete(ro);
    }
  }

  // Add visualizers for newly-discovered animated objects.
  for (const ro of live) {
    if (visualizers.has(ro)) continue;
    const viz = new SkinnedSkeletonVisualizer(scene.ctx, ro, bodyByObject.get(ro));
    visualizers.set(ro, viz);
    scene.objectManager.addObject(viz);
  }
}
